import * as z from 'zod';

export const CreateChatSettingsRequest = z.object({
  chatSettings: z
    .object({
      _id: z
        .string()
        .describe(
          'The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      greetingMessage: z
        .string()
        .describe(
          'Custom greeting message displayed when a visitor opens the AI chat.'
        )
        .max(50)
        .optional()
        .nullable(),
      chatEnabled: z
        .boolean()
        .describe(
          'Whether AI chat is enabled for this form.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the chat settings are updated.\nTo prevent conflicting changes, the current revision must be passed when updating the chat settings.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the chat settings were created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the chat settings were last updated.')
        .optional()
        .nullable(),
      summaryMessage: z
        .string()
        .describe(
          'Message that summarizes the contents of the form. Appears after the greeting message.'
        )
        .max(255)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      manualSummary: z
        .boolean()
        .describe(
          "Whether the summary message is manually set or AI-generated.\nWhen `false` the summary message is automatically generated by AI when the chat settings are saved.\nWhen `true`, the summary message can be manually set and won't be overwritten by AI.\n\nDefault: `false`"
        )
        .optional()
        .nullable(),
      personality: z
        .enum(['PROFESSIONAL', 'FRIENDLY', 'PLAYFUL', 'DIRECT'])
        .optional(),
    })
    .describe('Chat settings to be created.'),
});
export const CreateChatSettingsResponse = z.object({
  _id: z
    .string()
    .describe(
      'The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  greetingMessage: z
    .string()
    .describe(
      'Custom greeting message displayed when a visitor opens the AI chat.'
    )
    .max(50)
    .optional()
    .nullable(),
  chatEnabled: z
    .boolean()
    .describe('Whether AI chat is enabled for this form.\n\nDefault: `true`')
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the chat settings are updated.\nTo prevent conflicting changes, the current revision must be passed when updating the chat settings.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the chat settings were created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the chat settings were last updated.')
    .optional()
    .nullable(),
  summaryMessage: z
    .string()
    .describe(
      'Message that summarizes the contents of the form. Appears after the greeting message.'
    )
    .max(255)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  manualSummary: z
    .boolean()
    .describe(
      "Whether the summary message is manually set or AI-generated.\nWhen `false` the summary message is automatically generated by AI when the chat settings are saved.\nWhen `true`, the summary message can be manually set and won't be overwritten by AI.\n\nDefault: `false`"
    )
    .optional()
    .nullable(),
  personality: z
    .enum(['PROFESSIONAL', 'FRIENDLY', 'PLAYFUL', 'DIRECT'])
    .describe(
      'Personality of the AI when interacting with visitors.\nDefines the communication style of the AI chat responses.'
    )
    .optional(),
});
export const UpdateChatSettingsRequest = z.object({
  _id: z
    .string()
    .describe(
      'The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  chatSettings: z
    .object({
      _id: z
        .string()
        .describe(
          'The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      greetingMessage: z
        .string()
        .describe(
          'Custom greeting message displayed when a visitor opens the AI chat.'
        )
        .max(50)
        .optional()
        .nullable(),
      chatEnabled: z
        .boolean()
        .describe(
          'Whether AI chat is enabled for this form.\n\nDefault: `true`'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the chat settings are updated.\nTo prevent conflicting changes, the current revision must be passed when updating the chat settings.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the chat settings were created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the chat settings were last updated.')
        .optional()
        .nullable(),
      summaryMessage: z
        .string()
        .describe(
          'Message that summarizes the contents of the form. Appears after the greeting message.'
        )
        .max(255)
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      manualSummary: z
        .boolean()
        .describe(
          "Whether the summary message is manually set or AI-generated.\nWhen `false` the summary message is automatically generated by AI when the chat settings are saved.\nWhen `true`, the summary message can be manually set and won't be overwritten by AI.\n\nDefault: `false`"
        )
        .optional()
        .nullable(),
      personality: z
        .enum(['PROFESSIONAL', 'FRIENDLY', 'PLAYFUL', 'DIRECT'])
        .optional(),
    })
    .describe('Chat settings to be updated.'),
});
export const UpdateChatSettingsResponse = z.object({
  _id: z
    .string()
    .describe(
      'The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  greetingMessage: z
    .string()
    .describe(
      'Custom greeting message displayed when a visitor opens the AI chat.'
    )
    .max(50)
    .optional()
    .nullable(),
  chatEnabled: z
    .boolean()
    .describe('Whether AI chat is enabled for this form.\n\nDefault: `true`')
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the chat settings are updated.\nTo prevent conflicting changes, the current revision must be passed when updating the chat settings.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the chat settings were created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the chat settings were last updated.')
    .optional()
    .nullable(),
  summaryMessage: z
    .string()
    .describe(
      'Message that summarizes the contents of the form. Appears after the greeting message.'
    )
    .max(255)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  manualSummary: z
    .boolean()
    .describe(
      "Whether the summary message is manually set or AI-generated.\nWhen `false` the summary message is automatically generated by AI when the chat settings are saved.\nWhen `true`, the summary message can be manually set and won't be overwritten by AI.\n\nDefault: `false`"
    )
    .optional()
    .nullable(),
  personality: z
    .enum(['PROFESSIONAL', 'FRIENDLY', 'PLAYFUL', 'DIRECT'])
    .describe(
      'Personality of the AI when interacting with visitors.\nDefines the communication style of the AI chat responses.'
    )
    .optional(),
});
export const DeleteChatSettingsRequest = z.object({
  chatSettingsId: z
    .string()
    .describe('Chat settings ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteChatSettingsResponse = z.object({});
export const GetChatSettingsRequest = z.object({
  chatSettingsId: z
    .string()
    .describe('Chat settings ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetChatSettingsResponse = z.object({
  _id: z
    .string()
    .describe(
      'The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  greetingMessage: z
    .string()
    .describe(
      'Custom greeting message displayed when a visitor opens the AI chat.'
    )
    .max(50)
    .optional()
    .nullable(),
  chatEnabled: z
    .boolean()
    .describe('Whether AI chat is enabled for this form.\n\nDefault: `true`')
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the chat settings are updated.\nTo prevent conflicting changes, the current revision must be passed when updating the chat settings.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the chat settings were created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the chat settings were last updated.')
    .optional()
    .nullable(),
  summaryMessage: z
    .string()
    .describe(
      'Message that summarizes the contents of the form. Appears after the greeting message.'
    )
    .max(255)
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  manualSummary: z
    .boolean()
    .describe(
      "Whether the summary message is manually set or AI-generated.\nWhen `false` the summary message is automatically generated by AI when the chat settings are saved.\nWhen `true`, the summary message can be manually set and won't be overwritten by AI.\n\nDefault: `false`"
    )
    .optional()
    .nullable(),
  personality: z
    .enum(['PROFESSIONAL', 'FRIENDLY', 'PLAYFUL', 'DIRECT'])
    .describe(
      'Personality of the AI when interacting with visitors.\nDefines the communication style of the AI chat responses.'
    )
    .optional(),
});
export const QueryChatSettingsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const QueryChatSettingsResponse = z.object({
  chatSettings: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'The ID of the chat settings. The chat settings specified in this object apply to the form with a matching ID.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        greetingMessage: z
          .string()
          .describe(
            'Custom greeting message displayed when a visitor opens the AI chat.'
          )
          .max(50)
          .optional()
          .nullable(),
        chatEnabled: z
          .boolean()
          .describe(
            'Whether AI chat is enabled for this form.\n\nDefault: `true`'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the chat settings are updated.\nTo prevent conflicting changes, the current revision must be passed when updating the chat settings.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the chat settings were created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the chat settings were last updated.')
          .optional()
          .nullable(),
        summaryMessage: z
          .string()
          .describe(
            'Message that summarizes the contents of the form. Appears after the greeting message.'
          )
          .max(255)
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        manualSummary: z
          .boolean()
          .describe(
            "Whether the summary message is manually set or AI-generated.\nWhen `false` the summary message is automatically generated by AI when the chat settings are saved.\nWhen `true`, the summary message can be manually set and won't be overwritten by AI.\n\nDefault: `false`"
          )
          .optional()
          .nullable(),
        personality: z
          .enum(['PROFESSIONAL', 'FRIENDLY', 'PLAYFUL', 'DIRECT'])
          .describe(
            'Personality of the AI when interacting with visitors.\nDefines the communication style of the AI chat responses.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata about the pagination of the results.')
    .optional(),
});
